/**
 * Generisches OIDC via oidc-client-ts gegen die Lehner-Authentik-Instanz
 * (issue #6, ersetzt das Logto-SDK). Muster: Testerwerk AuthentikBridge.
 *
 * - Public Client mit PKCE (Authorization Code Flow, kein Client-Secret).
 * - Redirect-URI: <origin>/callback — diese URL muss in Authentik am
 *   Provider registriert sein (z. B. https://stage.my-lehner.app/callback).
 * - Logout: harter Redirect auf den Authentik-Invalidation-Flow
 *   (<idp-origin>/if/flow/default-invalidation-flow/) — der OAuth-
 *   End-Session-Endpunkt beendet die Authentik-Session NICHT zuverlässig.
 */

import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts"
import { create } from "zustand"

import { OIDC_ISSUER_URL, OIDC_CLIENT_ID, OIDC_SCOPE } from "@/lib/runtime-env"
import { refreshTokenFehlt, scopeAufbauen } from "@/lib/oidcScope"

// ---------------------------------------------------------------------------
// UserManager-Singleton (Callback-Seite braucht dieselbe Instanz)
// ---------------------------------------------------------------------------

let _userManager: UserManager | null = null

export function getUserManager(): UserManager {
  if (!_userManager) {
    _userManager = new UserManager({
      authority: OIDC_ISSUER_URL,
      client_id: OIDC_CLIENT_ID,
      redirect_uri: `${window.location.origin}/callback`,
      post_logout_redirect_uri: `${window.location.origin}/login`,
      // `offline_access` ist dabei — sonst gibt Authentik kein
      // Refresh-Token aus, und die Erneuerung fällt auf den versteckten
      // Rahmen zurück, den Authentik per `X-Frame-Options: DENY` selbst
      // blockiert. Ergebnis war: alle fünf Minuten neu anmelden.
      scope: scopeAufbauen(OIDC_SCOPE),
      userStore: new WebStorageStateStore({ store: window.localStorage }),

      automaticSilentRenew: true,
      // Rücksprungadresse der stillen Erneuerung: dieselbe wie beim
      // normalen Anmelden. Sie ist in Authentik bereits hinterlegt; eine
      // zweite hätte die Administration eintragen müssen.
      silent_redirect_uri: `${window.location.origin}/callback`,
      // 60 s vor Ablauf erneuern — genug Luft für einen zweiten Anlauf,
      // bevor das Token wirklich weg ist.
      accessTokenExpiringNotificationTimeInSeconds: 60,
    })

    // Wenn trotz `offline_access` kein Refresh-Token ankommt, liegt es fast
    // immer daran, dass der Scope am Provider in Authentik nicht
    // freigegeben ist. Das ist von hier aus nicht zu beheben — aber es soll
    // im Protokoll stehen, statt sich als „ich muss mich dauernd neu
    // anmelden" zu äußern.
    _userManager.events.addUserLoaded((user) => {
      if (refreshTokenFehlt(user)) {
        console.warn(
          "[auth] Kein Refresh-Token trotz offline_access — vermutlich ist der " +
            "Scope am Authentik-Provider nicht freigegeben. Ohne ihn endet die " +
            "Sitzung mit dem Zugriffstoken."
        )
      }
    })
  }
  return _userManager
}

// ---------------------------------------------------------------------------
// OIDC-Session-State (fuer useAuth/ProtectedRoute)
// ---------------------------------------------------------------------------

interface OidcState {
  /** Aktuelle OIDC-Session (null = nicht angemeldet). */
  oidcUser: User | null
  /** true bis die initiale Session-Wiederherstellung abgeschlossen ist. */
  isLoading: boolean
  setOidcUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
}

export const useOidcStore = create<OidcState>()((set) => ({
  oidcUser: null,
  isLoading: true,
  setOidcUser: (oidcUser) => set({ oidcUser }),
  setLoading: (isLoading) => set({ isLoading }),
}))

// ---------------------------------------------------------------------------
// Aktionen
// ---------------------------------------------------------------------------

/**
 * Login starten (Redirect zur Authentik-Anmeldung, PKCE).
 *
 * Der aufgerufene Ort wird mitgegeben, damit der Nutzer nach der Anmeldung
 * **dort** landet und nicht auf der Startseite (Nutzerwunsch 03.08.2026:
 * „nach Login direkt in die App").
 */
export function signIn(): void {
  const returnTo = window.location.pathname + window.location.search
  void getUserManager().signinRedirect({
    state: { returnTo: returnTo.startsWith("/callback") ? "/" : returnTo },
  })
}

/**
 * Abmelden — lokale Session entfernen, dann harter Redirect auf den
 * Authentik-Invalidation-Flow. signoutRedirect()/End-Session-Endpunkt
 * beenden die IdP-Session nicht zuverlässig (Befund Testerwerk), daher
 * IMMER der Invalidation-Flow.
 */
export async function signOutHard(): Promise<void> {
  // EINSTUFIG und bewusst OHNE end_session:
  //
  // 1. Die lokale Session ist nur der Token-Store im Browser -> removeUser().
  // 2. Entscheidend ist die ZENTRALE Authentik-Session (SSO). Ohne sie kann
  //    kein stiller Re-Login passieren.
  //
  // Warum nicht signoutRedirect()? Der Provider erlaubt (matching_mode
  // "strict") nur die Callback-URL; jede post_logout_redirect_uri wird
  // abgelehnt, der Rücksprung findet nie statt. Und der Provider-Logout
  // beendet ohnehin nur die App-Session, nicht die SSO-Session.
  //
  // Pfad: /flows/-/default/invalidation/ (antwortet 302, startet den Flow)
  // — NICHT /if/flow/<slug>/, das liefert nur die Oberfläche (200).
  try {
    await getUserManager().removeUser()
  } catch {
    /* lokaler Store ggf. schon leer */
  }
  const idpOrigin = new URL(OIDC_ISSUER_URL).origin
  window.location.href = `${idpOrigin}/flows/-/default/invalidation/`
}
